import React, { useMemo } from 'react';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import TextAreaField from '@grnhse/seedling/lib/birch/components/forms/fields/text_area_field';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import styled from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import EditIcon from '@grnhse/seedling/lib/birch/icons/edit';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix(
  'talent_matching.modals.calibration.ai_input_section'
);

type Props = {
  isLoading: boolean;
  parseSkillsError?: string;
};

const ReadOnlyUserPrompt = styled.div`
  background-color: ${FullPalette.WHITE};
  padding: ${Tokens.spacing['1']};
  border-radius: ${Tokens.spacing['0.5']};

  &:hover {
    cursor: pointer;
    background-color: ${Tokens.colors.hover};
  }

  .match {
    background-color: ${Tokens.colors.selected};
    padding: ${Tokens.spacing['0.25']} ${Tokens.spacing['1']};
    border-radius: ${Tokens.spacing['0.5']};
    line-height: 1.8rem;
    white-space: nowrap;
  }
`;

const HelperText = styled(SecondaryBody)`
  color: ${FullPalette.EVERGREEN_600};
`;

const ErrorText = styled(SecondaryBody)`
  color: ${Tokens.colors.error};
`;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightSkills = (
  prompt: string,
  skills: string[]
): (string | JSX.Element)[] => {
  if (!prompt || !skills || skills.length === 0) {
    return [prompt];
  }

  const escapedSkills = skills
    .map((skill) => escapeRegExp(skill.trim()))
    .filter((skill) => skill.length > 0)
    .sort((first, second) => second.length - first.length);

  if (escapedSkills.length === 0) {
    return [prompt];
  }

  const regex = new RegExp(escapedSkills.join('|'), 'gi');
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = regex.exec(prompt);

  while (match) {
    const startIndex = match.index;
    const endIndex = startIndex + match[0].length;
    const before = startIndex === 0 ? '' : prompt[startIndex - 1];
    const after = endIndex >= prompt.length ? '' : prompt[endIndex];
    const isBoundary =
      (!before || !/\w/.test(before)) && (!after || !/\w/.test(after));

    if (isBoundary) {
      if (startIndex > lastIndex) {
        parts.push(prompt.slice(lastIndex, startIndex));
      }

      parts.push(
        <span key={`${startIndex}-${endIndex}`} className="match">
          {prompt.slice(startIndex, endIndex)}
        </span>
      );

      lastIndex = endIndex;
    }

    match = regex.exec(prompt);
  }

  if (lastIndex < prompt.length) {
    parts.push(prompt.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [prompt];
};

const UserPrompt = ({ isLoading, parseSkillsError }: Props) => {
  const {
    watch,
    setValue,
    resetField,
    formState: { dirtyFields },
  } = useFormContext();
  const [extractedSkills, skills, userPrompt, userPromptMode] = watch([
    'extracted_skills',
    'skills',
    'userPrompt',
    'userPromptMode',
  ]);

  const toggleUserPromptMode = () => {
    resetField('userPrompt');
    setValue('userPromptMode', userPromptMode === 'view' ? 'edit' : 'view');
  };

  const highlightedPrompt = useMemo(
    () => highlightSkills(userPrompt, extractedSkills),
    [userPrompt, extractedSkills]
  );

  return userPromptMode === 'view' ? (
    <ReadOnlyUserPrompt onClick={toggleUserPromptMode}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Body>{highlightedPrompt}</Body>
        <IconButton
          onClick={toggleUserPromptMode}
          icon={EditIcon}
          size="md"
          label={t('edit')}
          flat
        />
      </Stack>
    </ReadOnlyUserPrompt>
  ) : (
    <Stack spacing={2}>
      <TextAreaField
        name="userPrompt"
        ariaLabel={t('description')}
        placeholder={t('placeholder')}
        autoResize={false}
        disabled={isLoading}
      />
      {parseSkillsError && (
        <ErrorText role="alert">{parseSkillsError}</ErrorText>
      )}
      {skills.length > 0 &&
        userPromptMode === 'edit' &&
        dirtyFields.userPrompt && (
          <HelperText>{t('overwrite_notice')}</HelperText>
        )}
    </Stack>
  );
};

export default UserPrompt;
